import type { ComponentType } from 'react';

// Adapts a named export from a lazy-loaded module into the `{ default }` shape React.lazy expects,
// so route entries that use `export function Page()` do not each need a `.then` shim.
const named =
  <TKey extends string, TMod extends Record<TKey, ComponentType<React.PropsWithChildren<any>>>>(
    load: () => Promise<TMod>,
    key: TKey
  ) =>
  () =>
    load().then((mod) => ({ default: mod[key] }));

export const Page = {
  Login: () => import('pages/Login/LoginSSO'),
  LoginMultiStep: () => import('pages/Login/LoginMultiStep'),
  LoginUpgrade: () => import('pages/Login/LoginUpgrade'),
  VerifyEmail: () => import('pages/2FA/VerifyEmail'),
  TwoFactorAuthentication: () => import('pages/2FA/2FA'),
  PasswordSecurityUpgrade: () => import('pages/PasswordUpgrade'),
  ChangePassword: () => import('pages/ResetPassword/ChangePassword'),
  OAuthDropOf: () => import('pages/OAuthDropOf'),
  SignupCreateAccount: () => import('pages/SignUp/CreateAccount'),
  SignUpPaymentCheckout: () => import('pages/SignUp/PaymentCheckout'),
  InviteSetPassword: () => import('pages/Invite/SetPassword'),
  SendResetEmail: () => import('pages/ResetPassword/SendResetEmail'),
  TwoFactorAuthenticationUnenroll: () => import('pages/2FA/Unenroll'),
  AuthRedirect: () => import('pages/AuthRedirect'),
  InternalError: () => import('pages/InternalError'),
  InviteSetAdminUserInfo: () => import('pages/Invite/SetAdminUserInfo'),
  SetupManager: () => import('pages/SignUp/SetupManager'),
  SetupOrganization: () => import('pages/SignUp/SetupOrganization'),
  SetupWaiting: () => import('pages/SignUp/SetupWaiting'),
  InvalidSubscription: () => import('pages/InvalidSubscription'),
  Videos: named(() => import('pages/Videos'), 'Videos'),
  StoryMakers: named(() => import('pages/Storymakers'), 'Storymakers'),
  Reviewers: () => import('pages/Reviewers'),
  FilmingGroups: () => import('pages/FilmingGroups'),
  TemplateDetail: named(() => import('pages/FilmingGroups/TemplateDetail'), 'TemplateDetail'),
  TemplateDetailSpeakingScript: named(
    () => import('pages/FilmingGroups/TemplateDetail/SpeakingScript'),
    'SpeakingScript'
  ),
  SettingsBrand: () => import('pages/Settings/Brand'),
  SettingsSubscription: () => import('pages/Settings/Subscription'),
  Organizations: named(() => import('pages/Organizations'), 'Organizations'),
  OrganizationDetailDetails: named(
    () => import('pages/Organizations/OrganizationDetail/Details'),
    'DetailsPage'
  ),
  OrganizationDetailFilmingGroups: named(
    () => import('pages/Organizations/OrganizationDetail/FilmingGroups'),
    'FilmingGroupsPage'
  ),
  OrganizationDetailFilmingGroupCreate: () =>
    import('pages/Organizations/OrganizationDetail/FilmingGroups/CreateFilmingGroup'),
  OrganizationDetailRosterMappings: named(
    () => import('pages/Organizations/OrganizationDetail/RosterMappings'),
    'RosterMappingsPage'
  ),
  OrganizationDetailRosterMappingEdit: named(
    () => import('pages/Organizations/OrganizationDetail/RosterMappings/EditRosterMapping'),
    'EditRosterMappingPage'
  ),
  AdminUsers: named(() => import('pages/AdminUsers'), 'AdminUsers'),
  SettingsOrganization: named(() => import('pages/Settings/Organization'), 'Organization'),
  SettingsAdminUsers: () => import('pages/Settings/AdminUsers'),
  AdminUserDetail: named(() => import('pages/AdminUsers/AdminUserDetail'), 'AdminUserDetail'),
  RosterUpload: named(() => import('pages/Storymakers/RosterUpload'), 'RosterUploadPage'),
  StorymakerDetail: named(() => import('pages/Storymakers/StorymakerDetail'), 'StorymakerDetail'),
  TemplateDetailAdminDetails: named(
    () => import('pages/Templates/TemplateDetailAdmin/Details'),
    'TemplateDetailAdminDetails'
  ),
  TemplateDetailAdminFilmingGuide: named(
    () => import('pages/Templates/TemplateDetailAdmin/FilmingGuide'),
    'TemplateDetailAdminFilmingGuide'
  ),
  TemplateDetailAdminSpeakingScript: named(
    () => import('pages/Templates/TemplateDetailAdmin/SpeakingScript'),
    'SpeakingScript'
  ),
  Templates: () => import('pages/Templates'),

  ExpiredPassword: () => import('pages/ExpiredPassword'),
  CreateVideo: named(() => import('pages/Videos/CreateVideo'), 'CreateVideo'),
  MediaDetail: named(() => import('pages/Videos/Detail'), 'MediaDetailWrapped'),
  Training: () => import('pages/Training'),
  NotFound: () => import('pages/NotFound'),
};
